import { Timestamp } from '@bufbuild/protobuf';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { projectDataClient } from '../../lib/connect/clients';
const RFI_RESPONSE_KEY = 'rfi-response';
function toTs(d) {
    return d ? Timestamp.fromDate(d) : undefined;
}
export function useRfiResponseWorkspace(rfiResponseProjectId) {
    return useQuery({
        queryKey: [
            RFI_RESPONSE_KEY,
            rfiResponseProjectId
        ],
        enabled: Boolean(rfiResponseProjectId) && rfiResponseProjectId !== 'new',
        queryFn: async ()=>{
            const resp = await projectDataClient.getRfiResponseWorkspace({
                rfiResponseProjectId
            });
            return resp;
        }
    });
}
export function useCreateRfiResponseProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.createRfiResponseProject({
                title: args.title,
                captureProjectId: args.captureProjectId ?? '',
                sourceNoticeId: args.sourceNoticeId ?? '',
                responseDue: toTs(args.responseDue),
                status: args.status ?? 'draft',
                notes: args.notes ?? '',
                clientRequestId: args.clientRequestId ?? ''
            });
            if (!resp.summary) throw new Error('CreateRfiResponseProject returned no summary');
            return resp.summary;
        },
        onSuccess: (_data, vars)=>{
            if (vars.captureProjectId) {
                queryClient.invalidateQueries({
                    queryKey: [
                        'capture',
                        vars.captureProjectId
                    ]
                });
            }
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to create RFI Response');
        }
    });
}
export function useUpdateRfiResponseProject() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.updateRfiResponseProject({
                rfiResponseProjectId: args.rfiResponseProjectId,
                title: args.title ?? '',
                sourceNoticeId: args.sourceNoticeId ?? '',
                responseDue: toTs(args.responseDue),
                status: args.status ?? '',
                // Field 7 is ignored on update. notesV2 carries presence: omit to
                // leave stored notes, send '' to clear. The page still always
                // passes notes from state on save.
                notesV2: args.notes,
                sourceNoticeIdV2: args.sourceNoticeIdV2,
                sections: args.sections ?? []
            });
            if (!resp.summary) throw new Error('UpdateRfiResponseProject returned no summary');
            return resp.summary;
        },
        onSuccess: (summary)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    RFI_RESPONSE_KEY,
                    summary.zouProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    RFI_RESPONSE_KEY,
                    summary.rfiResponseProjectId
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to update RFI Response');
        }
    });
}
export function useListRfiResponsesForCapture(captureProjectId) {
    return useQuery({
        queryKey: [
            'capture-rfi-responses',
            captureProjectId
        ],
        enabled: Boolean(captureProjectId),
        queryFn: async ()=>{
            const resp = await projectDataClient.listRfiResponsesForCapture({
                captureProjectId
            });
            return resp;
        }
    });
}
export function useLinkRfiResponseToCapture() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.linkRfiResponseToCapture({
                captureProjectId: args.captureProjectId,
                rfiResponseProjectId: args.rfiResponseProjectId
            });
            return resp.link;
        },
        onSuccess: (_data, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'capture',
                    vars.captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'capture-rfi-responses',
                    vars.captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    RFI_RESPONSE_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to link pursuit');
        }
    });
}
export function useLinkRfiResponseToOpportunity() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            const resp = await projectDataClient.linkRfiResponseToOpportunity({
                noticeId: args.noticeId,
                rfiResponseProjectId: args.rfiResponseProjectId
            });
            return resp.link;
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    RFI_RESPONSE_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to link opportunity');
        }
    });
}
export function useUnlinkRfiResponseFromOpportunity() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            await projectDataClient.unlinkRfiResponseFromOpportunity({
                noticeId: args.noticeId,
                rfiResponseProjectId: args.rfiResponseProjectId
            });
        },
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    RFI_RESPONSE_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to unlink opportunity');
        }
    });
}
export function useUnlinkRfiResponseFromCapture() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (args)=>{
            await projectDataClient.unlinkRfiResponseFromCapture({
                captureProjectId: args.captureProjectId,
                rfiResponseProjectId: args.rfiResponseProjectId
            });
        },
        onSuccess: (_data, vars)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'capture',
                    vars.captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'capture-rfi-responses',
                    vars.captureProjectId
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    RFI_RESPONSE_KEY
                ]
            });
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to unlink pursuit');
        }
    });
}
